import { StatusColor } from "~/constants/status"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { convertToStartOfWeekUTC } from "~/utils/datetime"
import {
  type DemandLifecycle,
  demandLifecycle,
  type LifecycleDates,
  planLifecycleStage as planLifecycleStageOf,
  type PlanLifecycleStage,
  salesHistoryStage as salesHistoryStageOf,
  type SalesHistoryStage,
} from "~/utils/demandLifecycle"
import { isPhasedOutVariant } from "~/utils/lifecycleUtils"

export {
  type DemandLifecycle,
  deriveDemandLifecycle,
  lifecycleStart,
  type PlanLifecycleStage,
  type SalesHistoryStage,
} from "~/utils/demandLifecycle"

export const DEMAND_LIFECYCLE_LABEL: Record<DemandLifecycle, string> = {
  upcoming: "Phasing in",
  live: "New",
  ongoing: "Ongoing",
  phasingOut: "Phasing out",
  phasedOut: "Phased out",
}

// Cycle order, which alphabetical order gets wrong.
export const DEMAND_LIFECYCLE_ORDER: DemandLifecycle[] = [
  "upcoming",
  "live",
  "ongoing",
  "phasingOut",
  "phasedOut",
]

export const DEMAND_LIFECYCLE_COLOR: Record<DemandLifecycle, StatusColor> = {
  upcoming: StatusColor.Information,
  live: StatusColor.Success,
  ongoing: StatusColor.Success,
  phasingOut: StatusColor.Warning,
  // Not red: a phased-out variant is history, not something to fix.
  phasedOut: StatusColor.Neutral,
}

/** The activation date demand2 reads: SkuMetadata's over Sku's, as its engine and Lifecycle do. */
export const demandActivatedFrom = (variant: Pick<IVariantSummary, "settings">) =>
  variant.settings?.demandActivatedFrom ?? null

/**
 * Its first sale and activation date when it sold in a week before the activation date. The
 * activation date stays its start; this only flags the sales before it for the planner to check.
 */
export const soldBeforeActivation = (
  variant: Pick<IVariantSummary, "settings" | "soldSince">,
): { firstSale: Date; activatedFrom: Date } | null => {
  const activated = demandActivatedFrom(variant)
  if (!activated || !variant.soldSince) return null
  const activatedFrom = new Date(activated)
  const firstSale = new Date(variant.soldSince)
  if (convertToStartOfWeekUTC(firstSale) >= convertToStartOfWeekUTC(activatedFrom)) return null
  return { firstSale, activatedFrom }
}

const datesOf = (variant: IVariantSummary): LifecycleDates => ({
  ...variant.settings,
  activatedFrom: demandActivatedFrom(variant),
  soldSince: variant.soldSince,
})

/** A variant's stage from its own row, as every demand2 surface reads it. */
export const variantLifecycle = (variant: IVariantSummary, asOf: Date): DemandLifecycle =>
  demandLifecycle(datesOf(variant), asOf)

export const SALES_HISTORY_ORDER: SalesHistoryStage[] = ["upcoming", "live", "mature"]

export const SALES_HISTORY_LABEL: Record<SalesHistoryStage, string> = {
  upcoming: DEMAND_LIFECYCLE_LABEL.upcoming,
  live: DEMAND_LIFECYCLE_LABEL.live,
  mature: "Mature",
}

// Mature reads as Ongoing.
export const SALES_HISTORY_COLOR: Record<SalesHistoryStage, StatusColor> = {
  upcoming: DEMAND_LIFECYCLE_COLOR.upcoming,
  live: DEMAND_LIFECYCLE_COLOR.live,
  mature: DEMAND_LIFECYCLE_COLOR.ongoing,
}

export const PLAN_LIFECYCLE_STAGE_ORDER: PlanLifecycleStage[] = [
  "ongoing",
  "phasingOut",
  "phasedOut",
]

/** A NOOS Assortment pill: its Sales history, or Phased out once it already is (NOOS_LIFECYCLE.md). */
export type NoosAssortmentPill = SalesHistoryStage | "phasedOut"

export const NOOS_PILL_ORDER: NoosAssortmentPill[] = [...SALES_HISTORY_ORDER, "phasedOut"]

export const NOOS_PILL_LABEL: Record<NoosAssortmentPill, string> = {
  ...SALES_HISTORY_LABEL,
  phasedOut: DEMAND_LIFECYCLE_LABEL.phasedOut,
}

export const NOOS_PILL_COLOR: Record<NoosAssortmentPill, StatusColor> = {
  ...SALES_HISTORY_COLOR,
  phasedOut: DEMAND_LIFECYCLE_COLOR.phasedOut,
}

export const salesHistoryStage = (variant: IVariantSummary, asOf: Date) =>
  salesHistoryStageOf(datesOf(variant), asOf)

/**
 * The pill a NOOS variant sits in: Phased out once it already is (right-now status), whatever its
 * Sales history, so it never counts as work; else its Sales history. Null: it starts after the plan.
 */
export function noosAssortmentPill(
  variant: IVariantSummary,
  asOf: Date,
): NoosAssortmentPill | null {
  if (isPhasedOutVariant(variant)) return "phasedOut"
  return salesHistoryStage(variant, asOf)
}

export const planLifecycleStage = (variant: IVariantSummary, asOf: Date) =>
  planLifecycleStageOf(datesOf(variant), asOf)
